import { toSafeLowerString } from '../utils/toSafeLowerString.js';
import { toSafeString } from '../utils/toSafeString.js';
const CIRRA_APPROVAL_POLICY = Object.freeze({
  untrusted: 'untrusted',
  onFailure: 'on-failure',
  onRequest: 'on-request',
  never: 'never'
});

const CIRRA_SANDBOX_MODE = Object.freeze({
  workspaceWrite: 'workspace-write',
  readOnly: 'read-only',
  dontAsk: 'dont-ask',
  fullAccess: 'full-access'
});

const CIRRA_WEB_SEARCH_MODE = Object.freeze({
  disabled: 'disabled',
  cached: 'cached',
  live: 'live'
});

const CIRRA_RESIDENCY_REQUIREMENT = Object.freeze({
  us: 'us'
});

const DEFAULT_CIRRA_ALLOWED_APPROVAL_POLICIES = Object.freeze([CIRRA_APPROVAL_POLICY.untrusted, CIRRA_APPROVAL_POLICY.onFailure, CIRRA_APPROVAL_POLICY.onRequest, CIRRA_APPROVAL_POLICY.never]);

const DEFAULT_CIRRA_ALLOWED_SANDBOX_MODES = Object.freeze([CIRRA_SANDBOX_MODE.workspaceWrite, CIRRA_SANDBOX_MODE.readOnly, CIRRA_SANDBOX_MODE.dontAsk, CIRRA_SANDBOX_MODE.fullAccess]);

const DEFAULT_CIRRA_ALLOWED_WEB_SEARCH_MODES = Object.freeze([CIRRA_WEB_SEARCH_MODE.disabled, CIRRA_WEB_SEARCH_MODE.cached, CIRRA_WEB_SEARCH_MODE.live]);

const DEFAULT_CIRRA_POLICY_REQUIREMENTS_VERSION = 'v1';

const toObject = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value;
};

const parseStringList = ({ value }) => {
  const out = [];
  const seen = new Set();
  const addEntry = (entry) => {
    const token = toSafeString(entry);

    if (!token || seen.has(token)) {
      return;
    }

    seen.add(token);
    out.push(token);
  };

  if (Array.isArray(value)) {
    for (const entry of value) {
      addEntry(entry);
    }

    return out;
  }

  const asString = toSafeString(value);

  if (!asString) {
    return out;
  }

  for (const token of asString.split(',')) {
    addEntry(token);
  }

  return out;
};

const parseBooleanOrNull = (value) => {
  if (typeof value === 'boolean') {
    return value;
  }

  const normalized = toSafeLowerString(value);

  if (!normalized) {
    return null;
  }

  if (['1', 'true', 'yes', 'on'].includes(normalized)) {
    return true;
  }

  if (['0', 'false', 'no', 'off'].includes(normalized)) {
    return false;
  }

  return null;
};

const parseIntegerOrNull = (value) => {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return null;
  }

  const normalized = Math.floor(parsed);

  if (normalized <= 0) {
    return null;
  }

  return normalized;
};

const normalizeEnumList = ({ value, allowedValues }) => {
  const allowedSet = new Set(allowedValues);
  const out = [];
  const seen = new Set();

  for (const rawEntry of parseStringList({ value })) {
    const normalized = toSafeLowerString(rawEntry);

    if (!allowedSet.has(normalized) || seen.has(normalized)) {
      continue;
    }

    seen.add(normalized);
    out.push(normalized);
  }

  return out;
};

const normalizeResidencyRequirement = ({ value }) => {
  const normalized = toSafeLowerString(value);

  if (!normalized) {
    return null;
  }

  if (normalized === CIRRA_RESIDENCY_REQUIREMENT.us) {
    return CIRRA_RESIDENCY_REQUIREMENT.us;
  }

  return null;
};

const normalizeCirraPolicyNetworkRequirements = ({ network }) => {
  const safeNetwork = toObject(network);

  if (!safeNetwork) {
    return null;
  }

  const out = {};
  const enabled = parseBooleanOrNull(safeNetwork.enabled);
  const httpPort = parseIntegerOrNull(safeNetwork.httpPort);
  const socksPort = parseIntegerOrNull(safeNetwork.socksPort);
  const allowUpstreamProxy = parseBooleanOrNull(safeNetwork.allowUpstreamProxy);
  const dangerouslyAllowNonLoopbackProxy = parseBooleanOrNull(safeNetwork.dangerouslyAllowNonLoopbackProxy);
  const dangerouslyAllowNonLoopbackAdmin = parseBooleanOrNull(safeNetwork.dangerouslyAllowNonLoopbackAdmin);
  const dangerouslyAllowAllUnixSockets = parseBooleanOrNull(safeNetwork.dangerouslyAllowAllUnixSockets);
  const allowLocalBinding = parseBooleanOrNull(safeNetwork.allowLocalBinding);
  const allowedDomains = parseStringList({ value: safeNetwork.allowedDomains });
  const deniedDomains = parseStringList({ value: safeNetwork.deniedDomains });
  const allowUnixSockets = parseStringList({ value: safeNetwork.allowUnixSockets });

  if (enabled !== null) {
    out.enabled = enabled;
  }

  if (httpPort !== null) {
    out.httpPort = httpPort;
  }

  if (socksPort !== null) {
    out.socksPort = socksPort;
  }

  if (allowUpstreamProxy !== null) {
    out.allowUpstreamProxy = allowUpstreamProxy;
  }

  if (dangerouslyAllowNonLoopbackProxy !== null) {
    out.dangerouslyAllowNonLoopbackProxy = dangerouslyAllowNonLoopbackProxy;
  }

  if (dangerouslyAllowNonLoopbackAdmin !== null) {
    out.dangerouslyAllowNonLoopbackAdmin = dangerouslyAllowNonLoopbackAdmin;
  }

  if (dangerouslyAllowAllUnixSockets !== null) {
    out.dangerouslyAllowAllUnixSockets = dangerouslyAllowAllUnixSockets;
  }

  if (allowLocalBinding !== null) {
    out.allowLocalBinding = allowLocalBinding;
  }

  if (allowedDomains.length > 0) {
    out.allowedDomains = allowedDomains;
  }

  if (deniedDomains.length > 0) {
    out.deniedDomains = deniedDomains;
  }

  if (allowUnixSockets.length > 0) {
    out.allowUnixSockets = allowUnixSockets;
  }

  if (Object.keys(out).length === 0) {
    return null;
  }

  return out;
};

const normalizeCirraPolicyRequirements = ({ allowedApprovalPolicies, allowedSandboxModes, allowedWebSearchModes, enforceResidency, network } = {}) => {
  const normalizedApprovalPolicies = normalizeEnumList({
    value: allowedApprovalPolicies,
    allowedValues: Object.values(CIRRA_APPROVAL_POLICY)
  });
  const normalizedSandboxModes = normalizeEnumList({
    value: allowedSandboxModes,
    allowedValues: Object.values(CIRRA_SANDBOX_MODE)
  });
  let normalizedWebSearchModes = normalizeEnumList({
    value: allowedWebSearchModes,
    allowedValues: Object.values(CIRRA_WEB_SEARCH_MODE)
  });

  if (normalizedApprovalPolicies.length === 0) {
    normalizedApprovalPolicies.push(...DEFAULT_CIRRA_ALLOWED_APPROVAL_POLICIES);
  }

  if (normalizedSandboxModes.length === 0) {
    normalizedSandboxModes.push(...DEFAULT_CIRRA_ALLOWED_SANDBOX_MODES);
  }

  if (normalizedWebSearchModes.length === 0) {
    normalizedWebSearchModes = [...DEFAULT_CIRRA_ALLOWED_WEB_SEARCH_MODES];
  }

  if (!normalizedWebSearchModes.includes(CIRRA_WEB_SEARCH_MODE.disabled)) {
    normalizedWebSearchModes.push(CIRRA_WEB_SEARCH_MODE.disabled);
  }

  return {
    allowedApprovalPolicies: normalizedApprovalPolicies,
    allowedSandboxModes: normalizedSandboxModes,
    allowedWebSearchModes: normalizedWebSearchModes,
    enforceResidency: normalizeResidencyRequirement({ value: enforceResidency }),
    network: normalizeCirraPolicyNetworkRequirements({ network })
  };
};

export { CIRRA_APPROVAL_POLICY, CIRRA_SANDBOX_MODE, CIRRA_WEB_SEARCH_MODE, CIRRA_RESIDENCY_REQUIREMENT, DEFAULT_CIRRA_ALLOWED_APPROVAL_POLICIES, DEFAULT_CIRRA_ALLOWED_SANDBOX_MODES, DEFAULT_CIRRA_ALLOWED_WEB_SEARCH_MODES, DEFAULT_CIRRA_POLICY_REQUIREMENTS_VERSION, normalizeCirraPolicyRequirements, normalizeCirraPolicyNetworkRequirements };
